import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Building2, Users, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { Card, SectionTitle } from "@/components/shared/primitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";

export default function TeamsPeople() {
  const qc = useQueryClient();
  const { data: teams } = useQuery({ queryKey: ["teams-list"], queryFn: () => api.get("/teams") });
  const { data: members } = useQuery({ queryKey: ["members-all"], queryFn: () => api.get("/members", { limit: 500 }) });
  const { data: offices } = useQuery({ queryKey: ["offices"], queryFn: () => api.get("/offices") });
  const { data: departments } = useQuery({ queryKey: ["departments"], queryFn: () => api.get("/departments") });

  const [archiving, setArchiving] = useState(null);
  const [newDept, setNewDept] = useState("");
  const [removingDept, setRemovingDept] = useState(null);
  const [addTeam, setAddTeam] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: "", department: "" });
  const [addPerson, setAddPerson] = useState(false);
  const [personForm, setPersonForm] = useState({ name: "", email: "", team_id: "", location: "", seniority: "" });
  const [resetting, setResetting] = useState(false);

  const teamRows = teams?.data?.rows || [];
  const teamOptions = teamRows.map((t) => ({ id: t.team_id, name: t.team }));
  const officeOptions = offices?.data || [];
  const deptOptions = departments?.data || [];
  const seniorityOptions = [...new Set((members?.data || []).map((m) => m.seniority).filter(Boolean))].sort();

  const invalidate = () => qc.invalidateQueries();

  const move = async (memberId, teamId) => {
    try { await api.patch(`/members/${memberId}/team`, { team_id: teamId }); toast.success("Moved."); invalidate(); }
    catch (e) { toast.error(e.message); }
  };
  const setOffice = async (memberId, location) => {
    try { await api.patch(`/members/${memberId}/location`, { location }); toast.success("Office updated."); invalidate(); }
    catch (e) { toast.error(e.message); }
  };
  const archive = async () => {
    try { await api.patch(`/members/${archiving.member_id}/archive`); toast.success(`${archiving.name} archived.`); invalidate(); }
    catch (e) { toast.error(e.message); }
    setArchiving(null);
  };

  const addDepartment = async () => {
    if (!newDept.trim()) return;
    try { await api.post("/departments", { name: newDept.trim() }); setNewDept(""); toast.success("Department added."); qc.invalidateQueries({ queryKey: ["departments"] }); }
    catch (e) { toast.error(e.message); }
  };
  const removeDepartment = async () => {
    try { await api.del(`/departments?name=${encodeURIComponent(removingDept)}`); toast.success("Department removed."); invalidate(); }
    catch (e) { toast.error(e.message); }
    setRemovingDept(null);
  };

  const createTeam = async () => {
    if (!teamForm.name.trim()) { toast.error("Give the team a name."); return; }
    try {
      await api.post("/teams", { name: teamForm.name.trim(), department: teamForm.department || undefined });
      toast.success("Team created."); setAddTeam(false); setTeamForm({ name: "", department: "" }); invalidate();
    } catch (e) { toast.error(e.message); }
  };
  const createPerson = async () => {
    if (!personForm.name.trim()) { toast.error("Enter the person's name."); return; }
    try {
      await api.post("/members", {
        name: personForm.name.trim(), email: personForm.email.trim() || undefined,
        team_id: personForm.team_id || undefined, location: personForm.location || undefined,
        seniority: personForm.seniority || undefined,
      });
      toast.success(`${personForm.name.trim()} added.`); setAddPerson(false);
      setPersonForm({ name: "", email: "", team_id: "", location: "", seniority: "" }); invalidate();
    } catch (e) { toast.error(e.message); }
  };

  const doReset = async () => {
    try {
      await api.post("/admin/reseed", {});
      toast.success("Demo data reset. Please sign in again.");
      localStorage.removeItem("token");
      setTimeout(() => { window.location.href = "/login"; }, 900);
    } catch (e) { toast.error(e.message); setResetting(false); }
  };

  return (
    <div className="space-y-6">
      {/* Departments */}
      <Card className="p-5" data-testid="departments-panel">
        <SectionTitle className="mb-1">Departments</SectionTitle>
        <p className="text-[13px] text-nb-ink2 mb-4">Departments are the umbrella practice groups (Litigation, Tax). Each holds one or more partner-led teams.</p>
        <div className="flex items-center gap-2 mb-5 max-w-md">
          <Input value={newDept} onChange={(e) => setNewDept(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addDepartment()} placeholder="Add a department, e.g. Banking" className="h-10" data-testid="dept-add-input" />
          <Button onClick={addDepartment} className="rounded-[10px] bg-nb-navy text-nb-cream font-semibold gap-2 shrink-0" data-testid="dept-add-btn"><Plus size={16} /> Add</Button>
        </div>
        <div className="flex flex-wrap gap-2 max-w-2xl">
          {deptOptions.length === 0 && <p className="text-[14px] text-nb-ink2">No departments yet.</p>}
          {deptOptions.map((dname) => (
            <span key={dname} className="inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-[10px] text-[14px] text-nb-ink" style={{ border: "1px solid var(--border-subtle)" }} data-testid={`dept-row-${dname}`}>
              <Building2 size={14} className="text-nb-ink2" /> {dname}
              <button onClick={() => setRemovingDept(dname)} className="p-1 text-nb-ink2 hover:text-[var(--neg-text)]" title="Remove" data-testid={`dept-remove-${dname}`}><Trash2 size={14} /></button>
            </span>
          ))}
        </div>
      </Card>

      {/* Teams */}
      <Card className="p-5" data-testid="teams-panel">
        <div className="flex items-center justify-between mb-1">
          <SectionTitle>Teams</SectionTitle>
          <Button onClick={() => setAddTeam(true)} className="rounded-[10px] bg-nb-navy text-nb-cream font-semibold gap-2" data-testid="add-team-btn"><Plus size={16} /> Add team</Button>
        </div>
        <p className="text-[13px] text-nb-ink2 mb-4">Small partner-led teams inside a department. Create one, then add people to it below.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-[14px]">
            <thead><tr className="text-[13px] text-nb-ink2 text-left border-b border-nb-border">
              <th className="py-2 font-medium">Team</th><th className="py-2 font-medium">Department</th><th className="py-2 font-medium">People</th>
            </tr></thead>
            <tbody>
              {teamRows.map((t) => (
                <tr key={t.team_id} className="border-b border-nb-border last:border-0" data-testid={`team-row-${t.team_id}`}>
                  <td className="py-2.5 text-nb-ink">{t.team}</td>
                  <td className="py-2.5 text-nb-ink2">{t.department || "-"}</td>
                  <td className="py-2.5 text-nb-ink2 tnum">{t.members}</td>
                </tr>
              ))}
              {teamRows.length === 0 && <tr><td colSpan={3} className="py-3 text-nb-ink2">No teams yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* People */}
      <Card className="p-5" data-testid="people-admin-panel">
        <div className="flex items-center justify-between mb-1">
          <SectionTitle>People</SectionTitle>
          <Button onClick={() => setAddPerson(true)} className="rounded-[10px] bg-nb-navy text-nb-cream font-semibold gap-2" data-testid="add-person-btn"><Plus size={16} /> Add person</Button>
        </div>
        <p className="text-[13px] text-nb-ink2 mb-4">
          Add people by hand, move them between teams, set their office, or archive a leaver. Archived people are hidden from lists but their past metrics stay correct.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-[14px]">
            <thead><tr className="text-[13px] text-nb-ink2 text-left border-b border-nb-border">
              <th className="py-2 font-medium">Name</th><th className="py-2 font-medium">Email</th>
              <th className="py-2 font-medium">Team</th><th className="py-2 font-medium">Office</th><th className="py-2 font-medium"></th>
            </tr></thead>
            <tbody>
              {(members?.data || []).map((m) => (
                <tr key={m.member_id} className="border-b border-nb-border last:border-0">
                  <td className="py-2.5 text-nb-ink">{m.name}</td>
                  <td className="py-2.5 text-nb-ink2">{m.email || "-"}</td>
                  <td className="py-2.5">
                    <Select value={m.team_id || ""} onValueChange={(v) => move(m.member_id, v)}>
                      <SelectTrigger className="h-9 w-[180px]" data-testid={`team-select-${m.member_id}`}><SelectValue placeholder={m.team || "No team"} /></SelectTrigger>
                      <SelectContent>{teamOptions.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="py-2.5">
                    <Select value={m.location || ""} onValueChange={(v) => setOffice(m.member_id, v)}>
                      <SelectTrigger className="h-9 w-[150px]" data-testid={`office-select-${m.member_id}`}><SelectValue placeholder={m.location || "No office"} /></SelectTrigger>
                      <SelectContent>{officeOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="py-2.5 text-right">
                    <button onClick={() => setArchiving(m)} className="text-[13px] text-nb-ink2 hover:text-[var(--neg-text)]" data-testid={`archive-member-${m.member_id}`}>Archive</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add team sheet */}
      <Sheet open={addTeam} onOpenChange={setAddTeam}>
        <SheetContent className="bg-nb-surface w-[440px] sm:max-w-[440px]">
          <SheetHeader>
            <SheetTitle className="text-nb-ink font-display">Add a team</SheetTitle>
            <SheetDescription className="text-nb-ink2">A small partner-led team inside a department.</SheetDescription>
          </SheetHeader>
          <div className="space-y-5 mt-6">
            <div><Label className="text-[13px]">Team name</Label><Input data-testid="team-name-input" value={teamForm.name} onChange={(e) => setTeamForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" placeholder="e.g. Litigation — Disputes" /></div>
            <div>
              <Label className="text-[13px]">Department</Label>
              <Select value={teamForm.department} onValueChange={(v) => setTeamForm((f) => ({ ...f, department: v }))}>
                <SelectTrigger className="mt-1 h-10" data-testid="team-dept-select"><SelectValue placeholder="Choose a department" /></SelectTrigger>
                <SelectContent>{deptOptions.map((dn) => <SelectItem key={dn} value={dn}>{dn}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={createTeam} className="w-full rounded-[10px] bg-nb-navy text-nb-cream font-semibold gap-2" data-testid="team-create-submit"><Users size={16} /> Create team</Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Add person sheet */}
      <Sheet open={addPerson} onOpenChange={setAddPerson}>
        <SheetContent className="bg-nb-surface w-[440px] sm:max-w-[440px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-nb-ink font-display">Add a person</SheetTitle>
            <SheetDescription className="text-nb-ink2">Create a directory entry by hand. No data-source sync needed.</SheetDescription>
          </SheetHeader>
          <div className="space-y-5 mt-6">
            <div><Label className="text-[13px]">Full name</Label><Input data-testid="person-name-input" value={personForm.name} onChange={(e) => setPersonForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" placeholder="e.g. Priya Nair" /></div>
            <div><Label className="text-[13px]">Work email</Label><Input data-testid="person-email-input" value={personForm.email} onChange={(e) => setPersonForm((f) => ({ ...f, email: e.target.value }))} className="mt-1" placeholder="priya.nair@firm.test" /></div>
            <div>
              <Label className="text-[13px]">Team</Label>
              <Select value={personForm.team_id} onValueChange={(v) => setPersonForm((f) => ({ ...f, team_id: v }))}>
                <SelectTrigger className="mt-1 h-10" data-testid="person-team-select"><SelectValue placeholder="No team" /></SelectTrigger>
                <SelectContent>{teamOptions.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[13px]">Office</Label>
              <Select value={personForm.location} onValueChange={(v) => setPersonForm((f) => ({ ...f, location: v }))}>
                <SelectTrigger className="mt-1 h-10" data-testid="person-office-select"><SelectValue placeholder="No office" /></SelectTrigger>
                <SelectContent>{officeOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {seniorityOptions.length > 0 && (
              <div>
                <Label className="text-[13px]">Seniority</Label>
                <Select value={personForm.seniority} onValueChange={(v) => setPersonForm((f) => ({ ...f, seniority: v }))}>
                  <SelectTrigger className="mt-1 h-10" data-testid="person-seniority-select"><SelectValue placeholder="Not set" /></SelectTrigger>
                  <SelectContent>{seniorityOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={createPerson} className="w-full rounded-[10px] bg-nb-navy text-nb-cream font-semibold gap-2" data-testid="person-create-submit"><Plus size={16} /> Add person</Button>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!archiving} onOpenChange={(o) => !o && setArchiving(null)}
        title={`Archive ${archiving?.name}?`}
        description="They will be hidden from lists and seat counts. Their past usage stays in reports so historical metrics remain correct."
        confirmLabel="Archive person" onConfirm={archive}
      />
      <ConfirmDialog
        open={!!removingDept} onOpenChange={(o) => !o && setRemovingDept(null)}
        title={`Remove ${removingDept}?`}
        description="Teams in this department will have their department cleared. The teams and their people stay."
        confirmLabel="Remove department" onConfirm={removeDepartment}
      />

      {/* Maintenance */}
      <Card className="p-5" data-testid="maintenance-panel">
        <SectionTitle className="mb-1">Reset demo data</SectionTitle>
        <p className="text-[13px] text-nb-ink2 mb-4">
          Rebuilds the full demo dataset — departments, partner-led teams, 80 people with offices, tools and usage. Use this if teams, departments or offices look empty or out of date. You will be signed out and can sign straight back in.
        </p>
        <Button onClick={() => setResetting(true)} className="rounded-[10px] gap-2 bg-nb-surface text-nb-ink font-semibold" style={{ border: "1px solid var(--border-control)" }} data-testid="reset-demo-btn">
          <RotateCcw size={16} /> Reset demo data
        </Button>
      </Card>

      <ConfirmDialog
        open={resetting} onOpenChange={(o) => !o && setResetting(false)}
        title="Reset all demo data?"
        description="This replaces every tool, team, department, person and usage record with a fresh demo dataset. You will be signed out and can log back in with the same account."
        confirmLabel="Reset data" onConfirm={doReset}
      />
    </div>
  );
}
